// `agent-chat`, not `chat`: the renderer lives with the agent block, and
// `blocks/chat` is the shell that uses it.
import { Markdown } from 'tailwind-v4/blocks/agent-chat'
import { cn } from 'tailwind-v4/lib/utils'

/**
 * What was said.
 *
 * DELIBERATELY NOT THE CHAT SHELL. `tailwind-v4/blocks/chat` is a page:
 * avatars, tool cards, a composer of its own, a scroll container of its
 * own. Here the conversation is one instrument on a floating surface that
 * already has a composer and a scroll area. Its `Markdown` renderer is
 * reused though, which is the bit actually worth having.
 *
 * NO BUBBLES. A box around every line, on glass, over a live page, is
 * three nested surfaces and it reads as clutter. Who said what is carried
 * by a name above the line and by the ink: the agent's words are the
 * foreground colour because they are the content, yours are quieter
 * because you already know what you said.
 *
 * MARKDOWN, because the agent writes it whether or not anything renders
 * it. Left as plain text it reads out `**Left side:**` with the asterisks
 * in, which is exactly what it looked like before this.
 */

export interface TranscriptTurn {
  id: string
  mine: boolean
  text: string
  /** Who said it, when it was neither you nor the agent. */
  author?: string
  files?: { name: string; url?: string; kind: 'image' | 'file' }[]
}

export function Transcript({
  turns,
  thinking,
}: {
  turns: TranscriptTurn[]
  /** The agent is working. Shown as a line of its own at the end. */
  thinking?: boolean
}) {
  if (turns.length === 0 && !thinking) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        Nothing said yet. Talk, type, or drop a file in.
      </p>
    )
  }
  return (
    <div className="flex min-w-0 flex-col gap-4">
      {turns.map((turn) => (
        <div key={turn.id} className="flex min-w-0 flex-col gap-1">
          <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground/70">
            {turn.mine ? 'You' : (turn.author ?? 'Assistant')}
          </span>
          {turn.text ? (
            turn.mine ? (
              // Yours is left as typed. Markdown on something somebody
              // wrote by hand means surprise formatting from a stray
              // asterisk.
              <p className="min-w-0 whitespace-pre-wrap break-words text-sm text-muted-foreground">
                {turn.text}
              </p>
            ) : (
              <Markdown text={turn.text} className="min-w-0 text-sm text-foreground" />
            )
          ) : null}
          {turn.files?.length ? (
            <div className="flex flex-wrap gap-1.5 pt-0.5">
              {turn.files.map((file) =>
                file.kind === 'image' && file.url ? (
                  <img
                    key={file.name}
                    src={file.url}
                    alt={file.name}
                    className="size-20 rounded-lg border border-border object-cover"
                  />
                ) : (
                  <span
                    key={file.name}
                    className="rounded-lg border border-border px-2 py-1 text-xs text-muted-foreground"
                  >
                    {file.name}
                  </span>
                ),
              )}
            </div>
          ) : null}
        </div>
      ))}

      {thinking ? <Thinking /> : null}
    </div>
  )
}

/**
 * The agent is working.
 *
 * It runs tools for several seconds before any text, and without this the
 * surface is indistinguishable from one that did not hear you — which is
 * the same complaint the spoken half had, answered the same way.
 *
 * Three dots rather than a spinner: a spinner is a promise about time it
 * cannot keep, and this is a thing that takes as long as it takes.
 */
export function Thinking() {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground/70">
        Assistant
      </span>
      <span className="flex items-center gap-1 py-1" aria-label="Working on it">
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className={cn(
              'size-1.5 rounded-full bg-muted-foreground/60',
              'animate-pulse',
            )}
            style={{ animationDelay: `${String(i * 160)}ms` }}
          />
        ))}
      </span>
    </div>
  )
}
